class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.currentSlideIndex = 0;
    this.slides = Array.from(this.querySelectorAll('.announcement-bar__message-slide'));
    this.autoRotateEnabled = this.getAutoRotatePreference() && !this.hasReducedMotion();
    this.changeSlideSpeed = parseInt(this.getAttribute('data-speed'), 10) * 1000;
    this.playButton = this.querySelector('.announcement-bar__controls button');
  }

  connectedCallback() {
    this.setupAutoRotate();
    this.updateSlideVisibility();
    this.updatePlayPauseButton();

    this.querySelector('.announcement-bar__button--prev').addEventListener('click', () => this.showPrevSlide());
    this.querySelector('.announcement-bar__button--next').addEventListener('click', () => this.showNextSlide());
    this.playButton.addEventListener('click', () => this.toggleAutoRotate());

    // Check the user's reduced motion preference when the component is connected
    if (this.hasReducedMotion()) {
      this.autoRotateEnabled = false;
      localStorage.setItem('autoRotate', 'false');
      this.updatePlayPauseButton();
    }
  }

  hasReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return prefersReducedMotion;
  }

  getAutoRotatePreference() {
    // Retrieve the preference from local storage, defaulting to "true" if not set
    const preference = localStorage.getItem('autoRotate');
    return preference !== null ? preference === 'true' : true;
  }

  setupAutoRotate() {
    if (this.autoRotateEnabled) {
      this.startAutoRotate();
    }
  }

  startAutoRotate() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = setInterval(() => {
      this.showNextSlide();
    }, this.changeSlideSpeed);
  }

  stopAutoRotate() {
    clearInterval(this.interval);
    this.interval = null;
  }

  toggleAutoRotate() {
    this.autoRotateEnabled = !this.autoRotateEnabled;
    localStorage.setItem('autoRotate', this.autoRotateEnabled);
    this.updatePlayPauseButton();

    if (this.autoRotateEnabled) {
      this.startAutoRotate();
    } else {
      this.stopAutoRotate();
    }
  }

  updatePlayPauseButton() {
    const isPlaying = this.autoRotateEnabled;
    const button = this.playButton;
    const playLabel = this.getAttribute('data-label-play');
    const pauseLabel = this.getAttribute('data-label-pause');
    const buttonSpan = button.querySelector('span');
    const svgContainer = button.querySelector('span + div.svg-container');
  
    if (!button) {
      return;
    }
  
    if (!buttonSpan || !svgContainer) {
      return;
    }
  
    // Update the button text
    buttonSpan.textContent = isPlaying ? pauseLabel : playLabel;
  
    // Update the aria-label
    button.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
  
    // Replace the SVG content
    const playSVG = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.3333 9.99998L8 6.33331V13.6666L14.3333 9.99998Z" fill="white"/>
        <circle cx="10" cy="10" r="9.5" stroke="white"/>
      </svg>`;
    const pauseSVG = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.33333 6.66669H8V13.3334H9.33333V6.66669Z" fill="white"/>
        <path d="M12 6.66669H10.6667V13.3334H12V6.66669Z" fill="white"/>
        <circle cx="10" cy="10" r="9.5" stroke="white"/>
      </svg>`;
  
    svgContainer.innerHTML = isPlaying ? pauseSVG : playSVG;
  }

  updateSlideVisibility() {
    this.slides.forEach((slide, index) => {
      const isVisible = index === this.currentSlideIndex;
      slide.classList.toggle('visible', isVisible);
      slide.setAttribute('aria-hidden', !isVisible);
      slide.setAttribute('tabindex', isVisible ? '0' : '-1');
    });
  }

  showPrevSlide() {
    this.currentSlideIndex = (this.currentSlideIndex - 1 + this.slides.length) % this.slides.length;
    this.animateSlideChange();
    this.restartAutoRotateIfEnabled();
  }

  showNextSlide() {
    this.currentSlideIndex = (this.currentSlideIndex + 1) % this.slides.length;
    this.animateSlideChange();
    this.restartAutoRotateIfEnabled();
  }

  // If the user clicks next/previous buttons, we want to restart the auto-rotate timer so we don't end up with conflicts of the speed threshold being met at the same time as the user manually changing slides
  restartAutoRotateIfEnabled() {
    if (this.autoRotateEnabled) {
      this.startAutoRotate();
    }
  }

  animateSlideChange() {
    this.slides.forEach(slide => slide.classList.add('changing'));
    this.updateSlideVisibility();
    setTimeout(() => {
      this.slides.forEach(slide => slide.classList.remove('changing'));
    }, 300);
  }

  disconnectedCallback() {
    this.stopAutoRotate();
  }
}

customElements.define('announcement-bar', AnnouncementBar);
